import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { SoundPermissionModalComponent } from '../sound-permission-modal/sound-permission-modal.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService, SoundService } from '@org/shared-services';

@Component({
  selector: 'app-sound-permission-banner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    <div *ngIf="showBanner" class="bg-gradient-to-r from-indigo-600 to-indigo-700 shadow-md">
      <div class="mx-auto max-w-7xl">
        <div class="flex items-center justify-between px-3 py-2 sm:px-4 lg:px-6">
          <!-- Left side: Icon and Text -->
          <div class="flex min-w-0 flex-1 items-center space-x-3">
            <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-800/80">
              <mat-icon class="h-5 w-5 text-white" aria-hidden="true">notifications_active</mat-icon>
            </span>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-white">
                <span class="block sm:hidden">Enable notification sounds</span>
                <span class="hidden sm:block">Enable notification sounds to stay informed about new tasks and updates</span>
              </p>
            </div>
          </div>

          <!-- Right side: Buttons -->
          <div class="ml-4 flex flex-shrink-0 items-center space-x-3">
            <button
              (click)="openSoundPermissionModal()"
              class="inline-flex items-center justify-center rounded-md border border-transparent bg-white px-3 py-1.5 text-sm font-medium text-indigo-600 shadow-sm transition-colors hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-indigo-600">
              Enable Sounds
            </button>
            <button
              (click)="dismissBanner()"
              type="button"
              class="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-indigo-500/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600">
              <span class="sr-only">Dismiss</span>
              <mat-icon class="h-5 w-5" aria-hidden="true">close</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 50;
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    }

    @media (max-width: 640px) {
      :host {
        position: static;
        width: 100%;
      }
    }
  `]
})
export class SoundPermissionBannerComponent implements OnInit, OnDestroy {
  showBanner = false;
  private permissionSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private soundService: SoundService
  ) {}

  ngOnInit() {
    this.updateBannerVisibility();

    this.permissionSubscription = this.soundService.permission$.subscribe(() => {
      this.updateBannerVisibility();
    });

    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (!isLoggedIn) {
          this.showBanner = false;
        }
      });
  }

  ngOnDestroy() {
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateBannerVisibility() {
    const soundPermission = this.soundService.currentPermission;
    const bannerDismissed = localStorage.getItem('soundPermissionBannerDismissed');
    const isLoggedIn = this.authService.isLoggedIn();
    this.showBanner = isLoggedIn && soundPermission === false && bannerDismissed !== 'true';
  }

  openSoundPermissionModal() {
    const dialogRef = this.dialog.open(SoundPermissionModalComponent, {
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.showBanner = false;
      } else {
        // If "Maybe Later" was clicked, ensure banner is shown
        this.updateBannerVisibility();
      }
    });
  }

  dismissBanner() {
    this.showBanner = false;
    localStorage.setItem('soundPermissionBannerDismissed', 'true');
  }
} 